import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthTenant, AuthUser, authApiClient } from './AuthApiClient';
import { navigateProduct } from '../shell/routes';

type AuthPhase = 'guest' | 'loading' | 'authenticated' | 'error';

interface ProductAuthContextValue {
  phase: AuthPhase;
  user: AuthUser | null;
  tenants: AuthTenant[];
  error: string | null;
  login: (returnTo?: string) => Promise<void>;
  logout: () => void;
  /**
   * The server said 401: drop the expired identity BEFORE showing the account page, so
   * the login form appears instead of "Dein Konto ist bereit" (#1758, Codex edge 4).
   * Drafts and recovery copies live in localStorage and are untouched.
   */
  expireSession: (returnTo?: string) => void;
  refresh: () => Promise<void>;
}

const ProductAuthContext = createContext<ProductAuthContextValue | null>(null);

export function ProductAuthProvider({ children }: { children: React.ReactNode }) {
  const [phase, setPhase] = useState<AuthPhase>(authApiClient.accessToken ? 'loading' : 'guest');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [tenants, setTenants] = useState<AuthTenant[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!authApiClient.accessToken) {
      setPhase('guest');
      setUser(null);
      setTenants([]);
      return;
    }
    setPhase('loading');
    setError(null);
    try {
      const [nextUser, nextTenants] = await Promise.all([
        authApiClient.getMe(),
        authApiClient.listTenants(),
      ]);
      setUser(nextUser);
      setTenants(nextTenants);
      setPhase('authenticated');
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : 'Anmeldung konnte nicht geladen werden.';
      setError(message);
      setPhase(message === 'AUTH_REQUIRED' || message === 'AUTH_EXPIRED' ? 'guest' : 'error');
    }
  }, []);

  useEffect(() => {
    if (authApiClient.accessToken) void refresh();
  }, [refresh]);

  const value = useMemo<ProductAuthContextValue>(() => ({
    phase,
    user,
    tenants,
    error,
    login: async (returnTo = '/app') => navigateProduct(`/auth?returnTo=${encodeURIComponent(returnTo)}`),
    logout: () => {
      authApiClient.logout();
      setUser(null);
      setTenants([]);
      setError(null);
      setPhase('guest');
    },
    expireSession: (returnTo = '/app') => {
      authApiClient.logout();
      setUser(null);
      setTenants([]);
      setError(null);
      setPhase('guest');
      navigateProduct(`/auth?returnTo=${encodeURIComponent(returnTo)}`);
    },
    refresh,
  }), [error, phase, refresh, tenants, user]);

  return <ProductAuthContext.Provider value={value}>{children}</ProductAuthContext.Provider>;
}

export function useProductAuth(): ProductAuthContextValue {
  const value = useContext(ProductAuthContext);
  if (!value) throw new Error('useProductAuth must be used inside ProductAuthProvider.');
  return value;
}
